import React, { useEffect, useState } from 'react';
import { managerAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, Building2, Home, Users, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#C85A42', '#38A169', '#3182CE', '#D69E2E', '#805AD5'];

export default function ManagerReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await managerAPI.getAnalytics();
        setData(res.data);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">No analytics data available</div>;

  const pieData = [
    { name: 'Occupied', value: data.occupancy_summary?.occupied_units || 0 },
    { name: 'Vacant', value: data.occupancy_summary?.vacant_units || 0 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" data-testid="reports-title">Reports & Analytics</h1>
        <p className="text-muted-foreground">Read-only overview of property performance</p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Total Properties
            </CardTitle>
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{data.occupancy_by_property?.length || 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Home className="w-4 h-4" /> Total Units
            </CardTitle>
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{data.occupancy_summary?.total_units || 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Occupancy Rate
            </CardTitle>
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{data.occupancy_summary?.overall_occupancy?.toFixed(1) || 0}%</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="w-4 h-4" /> Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{formatCurrency(data.monthly_revenue?.reduce((s, m) => s + m.revenue, 0) || 0)}</div></CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#C85A42]" /> Monthly Revenue</CardTitle>
            <CardDescription>Last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="revenue" fill="#C85A42" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Home className="w-5 h-5 text-[#C85A42]" /> Occupancy</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {pieData.map((entry, i) => <Cell key={entry.name || `pie-${i}`} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Property Occupancy Table */}
      <Card>
        <CardHeader><CardTitle>Property Occupancy Breakdown</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(data.occupancy_by_property || []).map((prop, i) => (
              <div key={prop.property_id || prop.property_name || `prop-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium">{prop.property_name}</p>
                  <p className="text-sm text-muted-foreground">{prop.occupied_units}/{prop.total_units} units occupied</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={prop.occupancy_rate > 80 ? 'default' : prop.occupancy_rate > 50 ? 'secondary' : 'destructive'}>
                    {prop.occupancy_rate}%
                  </Badge>
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-[#C85A42] rounded-full" style={{ width: `${prop.occupancy_rate}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Payments */}
      <Card>
        <CardHeader><CardTitle>Recent Payments</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(data.recent_payments || []).map((p, i) => (
              <div key={p.payment_id || `payment-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium">{p.tenant_name || 'Unknown'}</p>
                  <p className="text-sm text-muted-foreground">{p.payment_type} - {p.payment_method}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{formatCurrency(p.amount)}</p>
                  <Badge variant={p.status === 'verified' ? 'default' : p.status === 'pending' ? 'secondary' : 'destructive'}>{p.status}</Badge>
                </div>
              </div>
            ))}
            {(!data.recent_payments || data.recent_payments.length === 0) && (
              <p className="text-center text-muted-foreground py-4">No recent payments</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
